<template>
    <div id="posReceiptModal" class="modal">
        <div class="modal-dialog max-w-[340px] rounded-none" id="print">
            <div class="modal-header hidden-print py-5 gap-2">
                <button type="button" @click="reset"
                    class="modal-close w-full flex items-center justify-center gap-1.5 py-2 px-4 rounded-md text-gray-500 border bg-white">
                    <i class="lab lab-fill-close-circle lab-font-size-16"></i>
                    <span class="text-sm leading-5 font-client">{{ $t('button.close') }}</span>
                </button>
                <button type="button" v-print="printObj"
                    class="w-full flex items-center justify-center gap-1.5 py-2 px-4 rounded-md bg-primary">
                    <i class="lab lab-fill-printer lab-font-size-16 text-white"></i>
                    <span class="text-sm leading-5 font-client text-white">{{ $t('button.print_invoice') }}</span>
                </button>
            </div>
            <div class="modal-body text-gray-500">
                <div class="text-center pb-3.5 border-b border-dashed border-gray-400">
                    <h3 class="text-xl font-medium font-client mb-1">{{ company.company_name }}</h3>
                    <h4 class="text-xs font-normal">{{ company.company_address }}</h4>
                    <h5 class="text-xs font-normal">{{ $t('label.tel') }}: {{ company.company_calling_code }} {{
                        company.company_phone
                    }}</h5>
                </div>

                <table class="w-full my-1.5">
                    <tbody>
                        <tr>
                            <td class="text-xs text-left py-0.5">{{ $t('label.order_id') }}
                                #{{ order.order_serial_no }}
                            </td>
                        </tr>
                        <tr>
                            <td class="text-xs text-left py-0.5">{{ order.order_date }}</td>
                            <td class="text-xs text-right py-0.5">{{ order.order_time }} {{ $t('label.hrs') }}</td>
                        </tr>
                    </tbody>
                </table>

                <table class="w-full">
                    <thead class="border-t border-b border-dashed border-gray-400">
                        <tr>
                            <th scope="col" class="py-1 font-normal text-xs capitalize text-left w-8">
                                {{ $t('label.qty_tag') }}
                            </th>
                            <th scope="col"
                                class="py-1 font-normal text-xs capitalize flex items-center justify-between">
                                <span>{{ $t('label.product_description') }}</span>
                                <span>{{ $t('label.price') }}</span>
                            </th>
                        </tr>
                    </thead>

                    <tbody class="border-b border-dashed border-gray-400">
                        <tr v-if="orderProducts.length > 0" v-for="product in orderProducts" :key="product">
                            <td class="text-left font-normal align-top py-1">
                                <p class="text-xs leading-5">{{ product.quantity }}</p>
                            </td>
                            <td class="text-left font-normal align-top py-1">
                                <div class="flex items-center justify-between">
                                    <p class="text-xs leading-5">{{ product.product_name }}
                                    </p>
                                    <p class="text-xs leading-5">{{ product.subtotal_currency_price }}
                                    </p>
                                </div>
                                <p v-if="product.variation_names" class="text-xs leading-5 max-w-[200px]">
                                    {{ product.variation_names }}
                                </p>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div class="py-2">
                    <table class="w-full">
                        <tbody>
                            <tr>
                                <td class="text-xs text-left py-0.5 uppercase">{{ $t('label.subtotal') }}:
                                </td>
                                <td class="text-xs text-right py-0.5">
                                    {{ order.subtotal_currency_price }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-xs text-left py-0.5 uppercase">
                                    {{ $t('label.tax_fee') }}:
                                </td>
                                <td class="text-xs text-right py-0.5">
                                    {{ order.tax_currency_price }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-xs text-left py-0.5 uppercase">{{ $t('label.discount') }}:
                                </td>
                                <td class="text-xs text-right py-0.5">{{ order.discount_currency_price }}
                                </td>
                            </tr>

                            <tr>
                                <td class="text-xs text-left py-0.5 font-bold uppercase">
                                    {{ $t('label.total') }}:
                                </td>
                                <td class="text-xs text-right py-0.5 font-bold">
                                    {{ order.total_currency_price }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="text-xs py-2 border-t border-b border-dashed border-gray-400">
                    <table class="w-full">
                        <tbody>
                            <tr>
                                <td class="pt-1 pb-1 pr-1 align-top text-start">{{ $t('label.payment_type') }}: {{ order.pos_payment_method_name }}</td>
                                <td class="pt-1 pb-1 text-end" v-if="order.pos_received_amount > 0">
                                    <div>{{ $t('label.cash') }}: {{ order.pos_currency_received_amount}}</div>
                                    <span>{{ $t('label.change') }} : {{ order.change_currency_amount }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="text-center pt-2 pb-2">
                    <p class="text-[11px] leading-[14px] capitalize">
                        {{ $t('message.thank_you') }}
                    </p>
                    <p class="text-[11px] leading-[14px] capitalize">
                        {{ $t('message.please_come_again') }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import print from "vue3-print-nb";
import appService from "../../../services/appService";

export default {
    name: "ReceiptComponent",
    props: {
        order: Object
    },
    data() {
        return {
            printObj: {
                id: "print",
                popTitle: this.$t("menu.order_receipt"),
            },
        }
    },
    computed: {
        company: function () {
            return this.$store.getters['company/lists'];
        },
        orderProducts: function () {
            return this.$store.getters['posOrder/orderProducts'];
        },
    },
    mounted() {
        this.$store.dispatch("company/lists").then().catch();
    },
    methods: {
        reset: function () {
            appService.modalHide();
        },
    },
    directives: {
        print
    },
}
</script>
<style scoped>
@media print {
    .hidden-print {
        display: none !important;
    }
}
</style>